/**
 * Tiny floating popover that appears when the user hovers an AI proposal
 * decoration. Mirrors the "anchor to a DOM node and follow scroll" pattern
 * `@liveblocks/react-tiptap`'s FloatingToolbar uses, but for an arbitrary
 * decoration element rather than the editor selection.
 */
import { autoUpdate, computePosition, flip, shift } from '@floating-ui/dom'
import type { Editor } from '@tiptap/react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  widgetProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { getIslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import { SuggestionActionsToolbar } from '#src/components/rich-text-editor/SuggestionActionsToolbar.tsx'
import { SuggestionSourceExcerpt } from '#src/components/rich-text-editor/SuggestionSourceExcerpt.tsx'

const HOVER_HIDE_DELAY_MS = 120

export function AIProposalHoverPopover({
  editor,
  acceptSuggestion,
  onBeforeAccept,
  onAccept,
  onReject,
  inlineChoiceSources = false,
}: {
  editor: Editor | null
  /**
   * The full accept pipeline (design §1.3). When present (note editors), Accept
   * routes through it — the pipeline owns the Q5 guard, the recheck→dropped
   * routing, the stamp, the receipt, and side effects — instead of the bare
   * `acceptAIProposal` command + explicit `onAccept`. Absent on bare editors,
   * which keep the command-only accept path below.
   */
  acceptSuggestion?: (id: string) => void | Promise<void>
  /**
   * Re-read-before-insert accept guard (design Q5). Returns false to ABORT the
   * accept (e.g. a concurrent RN already accepted this suggestion, so inserting
   * again would duplicate clinical content). Absent → no guard (non-note editor).
   */
  onBeforeAccept?: (id: string) => boolean
  /**
   * Persist an EXPLICIT clinician accept (status='accepted' via the debounced
   * projection). Fired AFTER the editor accept command so the reconciler's
   * accepted→pending flip is gated: an explicit decidedAt write prevents a
   * forward edit from resetting the status back to pending.
   * Absent → no explicit write (non-note editor, or accept is content-derived).
   */
  onAccept?: (id: string) => void
  /**
   * Persist an EXPLICIT clinician reject (status='rejected' via the debounced
   * projection), so a dismissed suggestion can't resurrect on the next
   * re-paint/reload. Only fired for the Reject button — the guard-abort path
   * above drops a stale decoration without overwriting its 'accepted' record.
   * Absent → decoration-only reject (non-note editor).
   */
  onReject?: (ids: string[]) => void
  /**
   * The note draws a cited choice pick's source inline under its line, so this
   * popover leaves it out rather than show it twice. Off, the popover is where a
   * choice pick's source is shown.
   */
  inlineChoiceSources?: boolean
}): ReactNode {
  const [hovered, setHovered] = useState<{
    id: string
    label: string | null
  } | null>(null)
  const anchorRef = useRef<HTMLElement | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHideTimer = useCallback((): void => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const scheduleHide = useCallback((): void => {
    clearHideTimer()
    hideTimerRef.current = setTimeout(() => {
      setHovered(null)
      anchorRef.current = null
    }, HOVER_HIDE_DELAY_MS)
  }, [clearHideTimer])

  useEffect(() => {
    if (!editor) {
      return
    }
    const root = editor.view.dom

    const onOver = (event: MouseEvent): void => {
      const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
        '[data-ai-proposal-id]'
      )
      if (!target) {
        return
      }
      const id = target.dataset.aiProposalId
      if (!id) {
        return
      }
      // Suppress the hover toolbar while this proposal's island is open — the
      // island owns its own controls, and an Accept/Reject popover floating over
      // the editable region is noise. (2.b folds the popover into the island
      // toolbar and this special-case goes away.)
      if (getIslandRegistry(editor)?.isOpen(id)) {
        return
      }
      clearHideTimer()
      anchorRef.current = target
      setHovered({ id, label: target.dataset.aiProposalLabel ?? null })
    }

    const onOut = (event: MouseEvent): void => {
      const next = event.relatedTarget as Node | null
      if (next && popoverRef.current?.contains(next)) {
        return
      }
      const fromTarget = (event.target as HTMLElement | null)?.closest(
        '[data-ai-proposal-id]'
      )
      if (!fromTarget) {
        return
      }
      scheduleHide()
    }

    root.addEventListener('mouseover', onOver)
    root.addEventListener('mouseout', onOut)
    return () => {
      root.removeEventListener('mouseover', onOver)
      root.removeEventListener('mouseout', onOut)
      clearHideTimer()
    }
  }, [editor, clearHideTimer, scheduleHide])

  /* Clicking a suggestion card opens its island, and this popover is anchored to
   * that same card — so it has to retract or it floats over the surface the
   * clinician just started editing. The mouseover handler above already refuses
   * to re-show it while the island is open; this covers the popover that was
   * already showing at click time. */
  const islandRegistry = editor ? getIslandRegistry(editor) : null
  const hoveredId = hovered?.id ?? null
  useEffect(() => {
    if (!islandRegistry || !hoveredId) {
      return
    }
    return islandRegistry.subscribe(() => {
      if (islandRegistry.isOpen(hoveredId)) {
        setHovered(null)
        anchorRef.current = null
      }
    })
  }, [islandRegistry, hoveredId])

  useLayoutEffect(() => {
    if (!hovered || !anchorRef.current || !popoverRef.current) {
      return
    }
    const anchor = anchorRef.current
    const popover = popoverRef.current
    const update = (): void => {
      void computePosition(anchor, popover, {
        placement: 'top',
        middleware: [flip(), shift({ padding: 8 })],
      }).then(({ x, y }) => {
        popover.style.transform = `translate(${x}px, ${y}px)`
      })
    }
    update()
    return autoUpdate(anchor, popover, update)
  }, [hovered])

  if (!editor || !hovered) {
    return null
  }

  /* A choice pick paints no card — only a tint on the suggested option — so
   * unless the note draws its source inline, this popover is the one place it
   * can sit, beside the Accept and Reject it informs. Every other proposal draws
   * the same excerpt under its own card, and showing it here too would put the
   * source on screen twice. A pick that cited nothing gets nothing: the excerpt
   * renders null. */
  const hoveredProposal = AI_PROPOSALS_PLUGIN_KEY.getState(
    editor.state
  )?.proposals.get(hovered.id)
  const cardlessSource =
    !inlineChoiceSources && hoveredProposal && !widgetProposal(hoveredProposal)
      ? hoveredProposal
      : null

  /* The visible toolbar sits inside a transparent wrapper whose vertical
   * padding is the gap to the proposal. Making the gap padding (part of the
   * hovered element) rather than a floating-ui offset (empty space) lets the
   * cursor cross it without firing mouseleave, so the popover stays open on the
   * way to the buttons. Padding both sides covers the popover flipping above or
   * below; horizontal padding is omitted so it never overlaps a neighbor. */
  return (
    // mouse-only hover popover; the underlying suggestion decoration owns keyboard access.
    <div
      ref={popoverRef}
      // Opts this surface out of the island's click-outside dismiss: its Accept
      // reads the OPEN island's live content, so a pointerdown here must not
      // unmount the island first.
      data-ai-suggestion-surface
      className="absolute top-0 left-0 z-50 py-1.5"
      onMouseEnter={clearHideTimer}
      onMouseLeave={scheduleHide}
    >
      <SuggestionActionsToolbar
        label={hovered.label}
        onAccept={() => {
          // Note editors: the full accept pipeline (§1.3) owns the guard,
          // recheck, stamp, receipt, and side effects.
          if (acceptSuggestion) {
            void acceptSuggestion(hovered.id)
            setHovered(null)
            return
          }
          if (onBeforeAccept && !onBeforeAccept(hovered.id)) {
            // Guard aborted (already accepted elsewhere): drop the now-stale
            // decoration without re-inserting the content.
            editor
              .chain()
              .focus(null, { scrollIntoView: false })
              .rejectAIProposal(hovered.id)
              .run()
            setHovered(null)
            return
          }
          editor.chain().focus().acceptAIProposal(hovered.id).run()
          onAccept?.(hovered.id)
          setHovered(null)
        }}
        onReject={() => {
          // Capture the proposal BEFORE rejecting removes it, so Cmd+Z can
          // re-pend it (AIProposalUndoExtension). recordRejectedAIProposals
          // no-ops when the undo extension isn't installed (non-note editors).
          const proposal = AI_PROPOSALS_PLUGIN_KEY.getState(
            editor.state
          )?.proposals.get(hovered.id)
          editor
            .chain()
            .focus(null, { scrollIntoView: false })
            .rejectAIProposal(hovered.id)
            .run()
          if (proposal) {
            // Optional-call: the undo extension is present on note editors
            // (CollaborativeEditor installs it) but absent on bare editors.
            editor.commands.recordRejectedAIProposals?.([proposal])
          }
          onReject?.([hovered.id])
          setHovered(null)
        }}
      />
      {cardlessSource && (
        /* Given the toolbar's own opaque chrome, because the popover floats
         * over note text. On the excerpt rather than a wrapper, so a pick
         * that cited nothing — or whose source has not resolved — paints no
         * empty box. */
        <SuggestionSourceExcerpt
          citation={cardlessSource.citation}
          sourceProvenance={cardlessSource.sourceProvenance}
          className="mt-1 max-w-sm rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        />
      )}
    </div>
  )
}
